import React from "react";

import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";

import { Tooltip } from "components/Form/FormikControls/Tooltip";
import { ExclamationIcon } from "components/Icons";

import { ColumnType } from "components/AgGrid/constants";

import {
  CalculationBasis,
  CalculationBasisDisplayNameMap,
} from "constants/interest";

import { formatters } from "lib/agGrid";

export const InterestPreviewColId = {
  CALCULATION_BASIS: "00191d11-5957-4ee7-ac53-9a876c8f616f",
  INTEREST_AMOUNT: "800d5c0b-3ece-4877-9f9e-6c1912949035",
  INTEREST_RATE: "5b75ca02-f4f8-4070-b139-ebe0526af58c",
  DAYS_OF_INTEREST: "fca205e6-f895-44bd-93ee-dfe69859561b",
  CHARGE_FROM: "25a8f73f-8c41-4e95-a651-fde9fb31c71d",
  CHARGE_TO: "b2fa385d-a220-4577-9f53-7e71aee21f7d",
  WARNINGS: "09bd6df5-0b6f-4296-8bb6-20bfe781f2ff",
} as const;

const calculationBasisFormatter = ({
  value,
}: ValueFormatterParams<InterestPreviewRow, CalculationBasis>) =>
  (value && CalculationBasisDisplayNameMap[value]) || "";

const formatRatePercent = (rate: number) =>
  `${parseFloat((rate * 100).toFixed(2))}%`;

// PER_DOCUMENT has one rate, fixed by the document's own direction.
// BUSINESS_BALANCE has two configured rates -- the balance can sit on either
// side of zero within the period, each accruing at its own rate -- so there's
// no single number to show without misrepresenting whichever side didn't
// apply. Shown together instead, e.g. "10% Cr / 20% Dr".
const interestRateValueGetter = ({
  data,
}: ValueGetterParams<InterestPreviewRow>): string | null => {
  const preview = data?.interestPreview;
  if (!preview) {
    return null;
  }
  if (preview.calculationBasis === CalculationBasis.BUSINESS_BALANCE) {
    const parts = [
      preview.creditInterestRate &&
        `${formatRatePercent(preview.creditInterestRate)} Cr`,
      preview.debitInterestRate &&
        `${formatRatePercent(preview.debitInterestRate)} Dr`,
    ].filter(Boolean);
    return parts.length ? parts.join(" / ") : null;
  }
  return preview.interestRate != null
    ? formatRatePercent(preview.interestRate)
    : null;
};

// The displayed rate is text; sort on the underlying number instead.
const sortableInterestRate = (preview?: InterestPreviewData) =>
  preview?.interestRate ??
  preview?.debitInterestRate ??
  preview?.creditInterestRate ??
  null;

interface InterestPreviewRow {
  interestPreview: InterestPreviewData;
}

export const InterestPreviewColumnDef: Record<
  ValueInObj<typeof InterestPreviewColId>,
  ColDef<InterestPreviewRow>
> = {
  [InterestPreviewColId.CALCULATION_BASIS]: {
    colId: InterestPreviewColId.CALCULATION_BASIS,
    field: "interestPreview.calculationBasis",
    headerName: "Basis",
    valueFormatter: calculationBasisFormatter,
    lockVisible: true,
  },
  [InterestPreviewColId.INTEREST_AMOUNT]: {
    colId: InterestPreviewColId.INTEREST_AMOUNT,
    field: "interestPreview.interestToChargeCents",
    headerName: "Interest Amount",
    type: ColumnType.RIGHT_ALIGNED,
    enableValue: true,
    valueFormatter: formatters.signedCentsCreditDebitFormatter,
  },
  [InterestPreviewColId.INTEREST_RATE]: {
    colId: InterestPreviewColId.INTEREST_RATE,
    headerName: "Interest Rate",
    type: ColumnType.RIGHT_ALIGNED,
    valueGetter: interestRateValueGetter,
    comparator: (_valueA, _valueB, nodeA, nodeB) => {
      const rateA = sortableInterestRate(nodeA.data?.interestPreview);
      const rateB = sortableInterestRate(nodeB.data?.interestPreview);
      if (rateA === rateB) {
        return 0;
      }
      if (rateA === null) {
        return -1;
      }
      if (rateB === null) {
        return 1;
      }
      return rateA - rateB;
    },
  },
  [InterestPreviewColId.DAYS_OF_INTEREST]: {
    colId: InterestPreviewColId.DAYS_OF_INTEREST,
    field: "interestPreview.daysOfInterest",
    headerName: "Days of Interest",
  },
  [InterestPreviewColId.CHARGE_FROM]: {
    colId: InterestPreviewColId.CHARGE_FROM,
    field: "interestPreview.chargeFromDate",
    headerName: "Charge From",
    type: ColumnType.DATE,
  },
  [InterestPreviewColId.CHARGE_TO]: {
    colId: InterestPreviewColId.CHARGE_TO,
    field: "interestPreview.chargeToDate",
    headerName: "Charge To",
    type: ColumnType.DATE,
  },
  [InterestPreviewColId.WARNINGS]: {
    colId: InterestPreviewColId.WARNINGS,
    field: "interestPreview.warnings",
    headerName: "",
    sortable: false,
    filter: false,
    lockVisible: true,
    cellRenderer: ({
      value: warnings,
    }: ICellRendererParams<
      InterestPreviewRow,
      InterestPreviewData["warnings"]
    >) => {
      if (!warnings?.length) {
        return null;
      }
      return (
        <Tooltip
          title={warnings.map(warning => (
            <React.Fragment key={warning}>
              {warning}
              <br />
            </React.Fragment>
          ))}
          placement="top-start"
        >
          <ExclamationIcon color="warning" />
        </Tooltip>
      );
    },
  },
};
